import React, { Component } from 'react';

import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';
import styled from 'styled-components';

import { tableHelper } from '@builttocreate/engine-utils';

import FieldModes from '../../../../constants/FieldModes';
import FieldTableColumnTypes from '../../../../constants/FieldTableColumnTypes';
import FieldTableCustomColumnIds
  from '../../../../constants/FieldTableCustomColumnIds';
import FieldTypes from '../../../../constants/FieldTypes';
import computeStyleChanges from '../../../../lib/computeStyleChanges';
import TableDataCell from './TableDataCell';
import TableFieldBarcodeCell from './TableFieldBarcodeCell';
import TableFieldBlockCell from './TableFieldBlockCell';
import TableFieldDateCell from './TableFieldDateCell';
import TableFieldImageCell from './TableFieldImageCell';
import TableFieldMenuCell from './TableFieldMenuCell';
import TableFieldMultiSelectCell from './TableFieldMultiselectCell';
import TableFieldNumberCell from './TableFieldNumberCell';
import TableFieldSelectionCell from './TableFieldSelectionCell';
import TableFieldSignatureCell from './TableFieldSignatureCell';
import TableFieldTextCell from './TableFieldTextCell';
import TableRowDecoratorsCell from './TableRowDecoratorsCell';
import { filterHiddenTableColumnsSelector } from '../../../../lib/tableHelper';

import ThemeContext from '../../../../theme/ThemeContext';
import ThemeIcons from '../../../../theme/Icons';
import Icon from '../../../misc/Icon';

const { getCellOperand } = tableHelper;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${(props) => props.backgroundColor};
  border-top: ${(props) => (props.enableTopBorder ? props.border : 'transparent')};
`;

const StyledNumberCell = styled(TableDataCell)`
  vertical-align: middle;
  text-align: center;
  background-color: ${(props) => props.backgroundColor};
  border-right: ${(props) => (props.borderRight ? props.borderRight : 'transparent')};
  border-left: ${(props) => (props.borderLeft ? props.borderLeft : 'transparent')};
  border-bottom: ${(props) => (props.borderBottom ? props.borderBottom : 'transparent')};
  padding: 3px;
`;

class TableFieldRow extends Component {

  static propTypes = {

    id: PropTypes.string,
    fieldType: PropTypes.string,
    width: PropTypes.string,
    row: PropTypes.object,
    rowIndex: PropTypes.number,
    rowPath: PropTypes.string,
    columns: PropTypes.array,
    fieldPositionColumns: PropTypes.object,
    columnWidths: PropTypes.object,
    tableLookup: PropTypes.object,
    focused: PropTypes.string,
    disabled: PropTypes.bool,
    isMobileView: PropTypes.bool,
    fieldMode: PropTypes.string,

    enableTopBorder: PropTypes.bool,
    enableNumber: PropTypes.bool,
    enableMenu: PropTypes.bool,
    enableRowDecorators: PropTypes.bool,
    decoratorColumnWidth: PropTypes.number,
    rowDecorators: PropTypes.array,
    onRowDecoratorClick: PropTypes.func,

    //Styles
    primaryColor: PropTypes.string,
    dangerColor: PropTypes.string,

    //Styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    borderRadius: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,

    //Cell Action handlers
    onCellChange: PropTypes.func,
    onCellBlur: PropTypes.func,
    onCellFocus: PropTypes.func,
    onCellKeyPress: PropTypes.func,
    onCellFileUpload: PropTypes.func,
    onCellFileClick: PropTypes.func,
    onCellFileDelete: PropTypes.func,

    rowActions: PropTypes.array,
    onRowAction: PropTypes.func,

    //Measurement Action Handler
    onLoadCompleted: PropTypes.func,

    displayRequiredIndicators: PropTypes.bool,
  }

  static contextType = ThemeContext;

  static defaultProps = {
    tableLookup: {}
  }

  constructor(props) {
    super(props);
    this.state = {
      rowRef: null,
      rowLoaded: false,
      imageColumnsLoaded: {}
    };

  }

  shouldComponentUpdate(nextProps, nextState) {
    return shouldComponentUpdate(nextProps, this.props, nextState, this.state);
  }

  componentDidUpdate(prevProps) {

    const { row, columns, onLoadCompleted, fieldPositionColumns, view, fieldMode, docFields } = this.props;
    const { rowRef, imageColumnsLoaded, rowLoaded } = this.state;

    const filteredColumns = filterHiddenTableColumnsSelector({ columns, fieldPositionColumns, view, mode: fieldMode, docFields });
    const imageColumnsTotal = getTableImageColumns({ columns: filteredColumns }).length;


    if (onLoadCompleted && rowRef && !rowLoaded && ((Object.keys(imageColumnsLoaded).length === imageColumnsTotal) || imageColumnsTotal === 0)) {
      this.setState({ rowLoaded: true });
      onLoadCompleted(row._id, { height: rowRef.offsetHeight });
    }

    if (rowLoaded && !isEqual(row, prevProps.row)) {
      this.setState({ rowLoaded: false });
    }

  }

  render() {

    const { rowRef } = this.state;

    const {
      view,
      fieldType,
      id,
      row,
      rowIndex,
      rowPath,
      fieldPositionColumns,
      columns,
      columnWidths,
      focused,
      disabled,
      isMobileView,
      fieldMode,
      enableNumber,
      enableMenu,
      enableRowDecorators,
      decoratorColumnWidth,
      enableTopBorder,
      enableSystemFilePicker,
      displayRequiredIndicators,

      //SDK Styles
      primaryColor,
      dangerColor,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderRadius,
      borderColor,
      borderWidth,
      backgroundColor,
      padding,

      //Row Actions
      rowActions,
      onRowAction,
      rowDecorators,
      onRowDecoratorClick,

      //Cell Actions
      onCapture,
      onCellChange,
      onCellFocus,
      onCellBlur,
      onCellKeyPress,
      onCellFileUpload,
      onCellFileClick,
      onCellFileDelete,

    } = this.props;

    const styles = {
      backgroundColor,
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderRadius,
      borderColor,
      borderWidth,
      padding,
      primaryColor,
      dangerColor
    };

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const utilityColumns = [];
    if (enableNumber) utilityColumns.push({ _id: FieldTableCustomColumnIds.rowNumber, type: FieldTableCustomColumnIds.rowNumber, width: 46 });
    if (enableMenu) utilityColumns.push({ _id: FieldTableCustomColumnIds.rowMenu, type: FieldTableCustomColumnIds.rowMenu, width: 20 });
    if (enableRowDecorators) utilityColumns.push({ _id: FieldTableCustomColumnIds.rowDecorators, type: FieldTableCustomColumnIds.rowDecorators, width: decoratorColumnWidth || 40 });

    const formattedColumns = [...utilityColumns, ...columns];
    const filteredColumns = filterHiddenTableColumnsSelector({ columns: formattedColumns, fieldPositionColumns, view, mode: fieldMode, docFields: this.props.docFields });

    const cells = filteredColumns.map((column, columnIndex) => {

      const rowId = row._id;
      const fieldPositionColumn = fieldPositionColumns && fieldPositionColumns[column._id] ? fieldPositionColumns[column._id] : {};

      const cell = row.cells && row.cells[column._id] ? row.cells[column._id] : null;

      const defaultCalculatedCell = fieldType === FieldTypes.collection ? cell : undefined;
      const calculatedCell = row.calculatedCells && row.calculatedCells[column._id] ? row.calculatedCells[column._id] : defaultCalculatedCell;

      const cellKey = `${rowPath}_${column._id}`;
      const cellPath = `${rowPath}_${column._id}`;
      const isFocused = focused === cellPath;

      const multi = column.multi;

      let columnWidth = column.width;
      if (columnWidths && columnWidths[column._id] && !column.width) columnWidth = columnWidths[column._id];

      if (column._id === FieldTableCustomColumnIds.rowNumber) {

        /**
         * Handle cell borders for collection vs table field type
         */
        const borders = { borderRight: border, borderBottom: border };
        if (fieldType === FieldTypes.collection && columnIndex === 0) borders.borderLeft = border;
        if (fieldType === FieldTypes.collection) borders.borderTop = border;

        const rowIndexNode = displayRequiredIndicators ? (
          <Icon icon={ThemeIcons.settings.required} color={dangerColor} />
        ) : (rowIndex + 1);

        return (
          <StyledNumberCell
            disableHover
            key={cellKey}
            {...styles}
            {...{ fieldType, ...borders, backgroundColor }}
            width={columnWidth}
          >
            {rowIndexNode}
          </StyledNumberCell>
        );

      } else if (column._id === FieldTableCustomColumnIds.rowMenu) {
        return (
          <TableFieldMenuCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            rowId={row._id}
            rowIndex={rowIndex}
            rowPath={rowPath}
            options={rowActions}
            {...styles}
            padding={0}
            width={columnWidth}
            onRowAction={onRowAction}
          />
        );
      } else if (column._id === FieldTableCustomColumnIds.rowDecorators) {
        return (
          <TableRowDecoratorsCell
            key={cellKey}
            decorators={rowDecorators}
            fieldMode={fieldMode}
            rowId={row._id}
            onDecoratorClick={onRowDecoratorClick}
            borderColor={borderColor}
            borderWidth={borderWidth}
            backgroundColor={backgroundColor}
            width={columnWidth}
          />
        );
      } else if (column.type === FieldTableColumnTypes.dropdown) {
        return (
          <TableFieldSelectionCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            focused={isFocused}
            rowId={rowId}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            options={column.options}
            {...styles}
            width={columnWidth}
            onFocus={onCellFocus}
            onChange={onCellChange}
            onBlur={onCellBlur}
            onKeyPress={onCellKeyPress}
          />
        );
      } else if (column.type === FieldTableColumnTypes.image) {
        return (
          <TableFieldImageCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            enableSystemFilePicker={enableSystemFilePicker}
            focused={isFocused}
            rowId={rowId}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            multi={multi}
            {...styles}
            isMobileView={isMobileView}
            width={columnWidth}
            onUpload={this.handleCellUpload}
            onClick={onCellFileClick}
            onDelete={onCellFileDelete}
            onChange={onCellChange}
            onFocus={onCellFocus}
            onBlur={onCellBlur}
            onLoadCompleted={this.handleLoadCompleted}
            maxImageWidth={column.maxImageWidth}
            maxImageHeight={column.maxImageHeight}
          />
        );
      }
      else if (column.type === FieldTableColumnTypes.number) {
        return (
          <TableFieldNumberCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            focused={isFocused}
            rowId={rowId}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            {...styles}
            width={columnWidth}
            onFocus={onCellFocus}
            onChange={onCellChange}
            onBlur={onCellBlur}
            onKeyPress={onCellKeyPress}
            displayValue={calculatedCell}
          />
        );
      } else if (column.type === FieldTableColumnTypes.date) {
        return (
          <TableFieldDateCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            focused={isFocused}
            rowId={rowId}
            timezone={row.tz}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            {...styles}
            width={columnWidth}
            onFocus={onCellFocus}
            onChange={onCellChange}
            onBlur={onCellBlur}
            onKeyPress={onCellKeyPress}
            displayValue={calculatedCell}
            format={fieldType === FieldTypes.collection ? column.format : fieldPositionColumn.format}
          />
        );

      } else if (column.type === FieldTableColumnTypes.block) {
        return (
          <TableFieldBlockCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            focused={isFocused}
            rowId={rowId}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            displayValue={calculatedCell}
            {...styles}
            width={columnWidth}
            onFocus={onCellFocus}
            onChange={onCellChange}
            onBlur={onCellBlur}
            onKeyPress={onCellKeyPress}
            fieldMode={fieldMode}
          />
        );
      } else if (column.type === FieldTableColumnTypes.multiSelect) {
        return (
          <TableFieldMultiSelectCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            focused={isFocused}
            rowId={rowId}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            options={column.options}
            {...styles}
            width={columnWidth}
            onFocus={onCellFocus}
            onChange={onCellChange}
            onBlur={onCellBlur}
            onKeyPress={onCellKeyPress}
          />
        );
      } else if (column.type === FieldTableColumnTypes.barcode) {
        return (
          <TableFieldBarcodeCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            focused={isFocused}
            rowId={rowId}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            displayValue={calculatedCell}
            {...styles}
            width={columnWidth}
            onFocus={onCellFocus}
            onChange={onCellChange}
            onBlur={onCellBlur}
            onKeyPress={onCellKeyPress}
            onCapture={onCapture}
          />
        );
      } else if (column.type === FieldTableColumnTypes.signature) {
        return (
          <TableFieldSignatureCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled || fieldMode === FieldModes.edit}
            focused={isFocused}
            rowId={rowId}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            displayValue={calculatedCell}
            {...styles}
            width={columnWidth}
            onFocus={onCellFocus}
            onChange={onCellChange}
            onBlur={onCellBlur}
            onKeyPress={onCellKeyPress}
            onLoadCompleted={this.handleLoadCompleted}
            maxImageWidth={column.maxImageWidth}
            maxImageHeight={column.maxImageHeight}
            fieldMode={fieldMode}
          />
        );
      }
      else {
        return (
          <TableFieldTextCell
            id={id}
            key={cellKey}
            cellKey={cellKey}
            disabled={disabled}
            focused={isFocused}
            rowId={rowId}
            columnId={column._id}
            columnType={column.type}
            columnIdentifier={column.identifier}
            value={cell}
            displayValue={calculatedCell}
            {...styles}
            width={columnWidth}
            onFocus={onCellFocus}
            onChange={onCellChange}
            onBlur={onCellBlur}
            onKeyPress={onCellKeyPress}
          />
        );
      }

    });

    return (
      <StyledRow
        ref={(e) => {
          if (e && !rowRef) this.setState({ rowRef: e });
        }}
        key={row._id}
        data-testid={`${id}-${row._id}-${rowIndex}-table-field-row`}
        {...{ border, backgroundColor, enableTopBorder }}
      >
        {cells}
      </StyledRow>
    );

  }

  handleLoadCompleted = (columnId) => {
    const { imageColumnsLoaded } = this.state;
    if (!imageColumnsLoaded[columnId]) this.setState((prevState) => ({ imageColumnsLoaded: { ...prevState.imageColumnsLoaded, [columnId]: true } }));
  }

  handleCellUpload = (rowId, columnId, value, loadingCallback, errorCallback) => {

    const { onCellFileUpload } = this.props;
    const { imageColumnsLoaded } = this.state;

    const nextImageColumnsLoaded = { ...imageColumnsLoaded };
    delete nextImageColumnsLoaded[columnId];

    this.setState({ imageColumnsLoaded: nextImageColumnsLoaded });

    if (onCellFileUpload) onCellFileUpload(rowId, columnId, value, loadingCallback, errorCallback);

  }

}

export const getTableImageColumns = createSelector(
  (state) => state.columns,
  (columns) => {

    /**
     * Return all image columns that exist inside the tableColumnOrder
     */
    return columns.filter((column) => column.type === FieldTableColumnTypes.image || column.type === FieldTableColumnTypes.signature);

  }
);

const shouldComponentUpdate = (nextProps, props, nextState, state) => {

  const { width, focused, columns, columnWidths, row, rowActions, rowPath, tableLookup, disabled, displayRequiredIndicators, fieldPositionColumns, rowDecorators } = props;
  const { imageColumnsLoaded, rowRef } = state;

  const stylesChanged = computeStyleChanges(nextProps, props);

  /**
   * Row is impacted if either the previously editing cell belonged to this
   * row or the next editing cell belongs to this row.
   */
  const editingCellImpactsRow = (focused && focused.indexOf(rowPath) !== -1)
    || (nextProps.focused && nextProps.focused.indexOf(rowPath) !== -1);

  /**
   * Only pass table lookup for each row.
   */
  if (
    (focused !== nextProps.focused && editingCellImpactsRow)
    || disabled !== nextProps.disabled
    || width !== nextProps.width
    || width !== nextProps.width
    || !isEqual(fieldPositionColumns, nextProps.fieldPositionColumns)
    || !isEqual(columns, nextProps.columns)
    || !isEqual(columnWidths, nextProps.columnWidths)
    || !isEqual(tableLookup, nextProps.tableLookup)
    || !isEqual(row, nextProps.row)
    || !isEqual(rowActions, nextProps.rowActions)
    || stylesChanged
    || !isEqual(rowDecorators, nextProps.rowDecorators)
    || imageColumnsLoaded !== nextState.imageColumnsLoaded
    || rowRef !== nextState.rowRef
    || displayRequiredIndicators !== nextProps.displayRequiredIndicators
  ) {
    return true;
  } else {
    return false;
  }

};

export { shouldComponentUpdate };

export default TableFieldRow;
